import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Sparkles } from "lucide-react";
import { CARD_THEMES } from "@/components/VirtualCryptoCard";
import api from "@/lib/api";
import { toast } from "sonner";

const THEME_SWATCH = {
  aurora: "linear-gradient(135deg,#00F0FF,#7A5CFF,#FF007F)",
  sunset: "linear-gradient(135deg,#FFE600,#FF7A00,#FF007F)",
  matrix: "linear-gradient(135deg,#00FF9D,#00C2FF,#0047FF)",
  molten: "linear-gradient(135deg,#FF007F,#FF4D00,#FFE600)",
};

export default function IssueCardDialog({ open, onOpenChange, onCreated, users = [], prefill = null }) {
  const [name, setName] = useState("");
  const [nickname, setNickname] = useState("");
  const [password, setPassword] = useState("");
  const [deposit, setDeposit] = useState("500");
  const [theme, setTheme] = useState("aurora");
  const [targetEmail, setTargetEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [heavyMode, setHeavyMode] = useState(false);
  const [staticSig, setStaticSig] = useState("");
  const [movingSig, setMovingSig] = useState("");
  const [lockCard, setLockCard] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [creditLimit, setCreditLimit] = useState("0");
  const [binId, setBinId] = useState("none");
  const [bins, setBins] = useState([]);

  useEffect(() => {
    if (open) {
      if (prefill?.email) setTargetEmail(prefill.email);
      else if (users.length && !targetEmail) setTargetEmail(users[0].email);
      if (prefill?.nickname) setNickname(prefill.nickname);
      if (prefill?.deposit) setDeposit(String(prefill.deposit));
      api.get("/admin/bins").then((r) => setBins(r.data.filter((b) => b.active))).catch(() => {});
    }
  }, [open, users, targetEmail, prefill]);

  const reset = () => {
    setName("");
    setNickname("");
    setPassword("");
    setDeposit("500");
    setTheme("aurora");
    setTargetEmail("");
    setHeavyMode(false);
    setStaticSig("");
    setMovingSig("");
    setLockCard(false);
    setCurrency("USD");
    setCreditLimit("0");
    setBinId("none");
  };

  const handleCreate = async () => {
    if (!targetEmail) {
      toast.error("Select a recipient");
      return;
    }
    if (!name || !nickname.trim() || password.length < 4) {
      toast.error("Enter a name, a merchant nickname, and a password of at least 4 characters");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        cardholder_name: name,
        nickname: nickname.trim(),
        password,
        initial_deposit: parseFloat(deposit) || 0,
        color_theme: theme,
        target_email: targetEmail,
        currency,
        credit_limit: parseFloat(creditLimit) || 0,
        bin_id: binId === "none" ? null : binId,
      };
      if (heavyMode) {
        const clean = (s) => (s || "").trim().toUpperCase();
        const sMap = clean(staticSig);
        const mMap = clean(movingSig);
        if (sMap && sMap.length !== 16) return toast.error("Static signature must be exactly 16 characters") || setLoading(false);
        if (mMap && mMap.length !== 16) return toast.error("Moving signature must be exactly 16 characters") || setLoading(false);
        if (sMap) payload.static_signature = sMap;
        if (mMap) payload.moving_signature = mMap;
        payload.lock = lockCard;
      }
      const res = await api.post("/cards", payload);
      toast.success(`Card issued to ${targetEmail}`);
      onCreated(res.data);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to issue card");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="bg-[#121216] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#00F0FF]" /> Issue a Card
          </DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Issuer-only. A unique 32-digit signature is minted — 16 public, 16 sealed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Issue to (account)</Label>
            <Select value={targetEmail} onValueChange={setTargetEmail}>
              <SelectTrigger data-testid="card-recipient-select" className="bg-[#1A1A20] border-white/10 text-white h-11">
                <SelectValue placeholder="Select a user" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A20] border-white/10 text-white">
                {users.map((u) => (
                  <SelectItem key={u.user_id} value={u.email} className="focus:bg-white/10 focus:text-white">
                    {u.name} · {u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Cardholder name</Label>
            <Input
              data-testid="card-name-input"
              placeholder="e.g. Alex Rivera"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-[#1A1A20] border-white/10 text-white h-11"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Merchant nickname (identifier)</Label>
            <Input
              data-testid="card-nickname-input"
              placeholder="e.g. @novacoffee"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="bg-[#1A1A20] border-white/10 text-white h-11 font-mono-sig"
            />
            <p className="text-[10px] text-[#52525B]">Unique handle merchants use to identify this account.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Card password</Label>
              <Input
                data-testid="card-password-input"
                type="password"
                placeholder="min 4 chars"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#1A1A20] border-white/10 text-white h-11 font-mono-sig"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Initial deposit</Label>
              <Input
                data-testid="card-deposit-input"
                type="number"
                value={deposit}
                onChange={(e) => setDeposit(e.target.value)}
                className="bg-[#1A1A20] border-white/10 text-white h-11 font-mono-sig"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="card-currency-select" className="bg-[#1A1A20] border-white/10 text-white h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A20] border-white/10 text-white">
                  <SelectItem value="USD" className="focus:bg-white/10 focus:text-white">USD · $</SelectItem>
                  <SelectItem value="EUR" className="focus:bg-white/10 focus:text-white">EUR · €</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Credit limit</Label>
              <Input
                data-testid="card-credit-limit-input"
                type="number"
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)}
                className="bg-[#1A1A20] border-white/10 text-white h-11 font-mono-sig"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Issue under BIN (optional)</Label>
            <Select value={binId} onValueChange={setBinId}>
              <SelectTrigger data-testid="card-bin-select" className="bg-[#1A1A20] border-white/10 text-white h-11">
                <SelectValue placeholder="No BIN · random number" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A20] border-white/10 text-white">
                <SelectItem value="none" className="focus:bg-white/10 focus:text-white">No BIN · random number</SelectItem>
                {bins.map((b) => (
                  <SelectItem key={b.id} value={b.id} className="focus:bg-white/10 focus:text-white">
                    {b.bin}{b.label ? ` · ${b.label}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#52525B]">Card numbers start with your BIN. Manage BINs in the Admin → BINs tab.</p>
          </div>

          <div className="space-y-2">
            <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Card theme</Label>
            <div className="flex gap-3">
              {CARD_THEMES.map((t) => (
                <button
                  key={t}
                  data-testid={`theme-${t}-button`}
                  onClick={() => setTheme(t)}
                  className={`h-11 flex-1 rounded-xl transition-transform ${theme === t ? "ring-2 ring-white scale-105" : "ring-1 ring-white/10"}`}
                  style={{ background: THEME_SWATCH[t] }}
                  aria-label={t}
                />
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-[#FFE600]/20 bg-[#FFE600]/[0.04] p-3">
            <button
              type="button"
              data-testid="heavy-mode-toggle"
              onClick={() => setHeavyMode((v) => !v)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="font-mono-sig text-[10px] tracking-[0.25em] uppercase text-[#FFE600]">Manual heavy production</span>
              <span className="text-[#FFE600] text-sm">{heavyMode ? "−" : "+"}</span>
            </button>
            {heavyMode && (
              <div className="space-y-3 mt-3">
                <p className="text-[10px] text-[#A0A0AB] leading-relaxed">
                  Set the exact 32-char constitution (16 static + 16 moving, A–Z / 0–9). Uniform = heavier. Leave blank to auto-mint. Lock reserves it for the highest bidder.
                </p>
                <Input
                  data-testid="static-sig-input"
                  placeholder="Static half · 16 chars (e.g. 0000000000000000)"
                  value={staticSig}
                  maxLength={16}
                  onChange={(e) => setStaticSig(e.target.value)}
                  className="bg-[#1A1A20] border-white/10 text-white h-11 font-constitution tracking-widest"
                />
                <Input
                  data-testid="moving-sig-input"
                  placeholder="Moving half · 16 chars"
                  value={movingSig}
                  maxLength={16}
                  onChange={(e) => setMovingSig(e.target.value)}
                  className="bg-[#1A1A20] border-white/10 text-white h-11 font-constitution tracking-widest"
                />
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="lock-card-checkbox"
                    type="checkbox"
                    checked={lockCard}
                    onChange={(e) => setLockCard(e.target.checked)}
                    className="accent-[#FF007F] w-4 h-4"
                  />
                  <span className="text-xs text-[#A0A0AB]">Lock &amp; reserve for highest bidder (cannot transact until unlocked)</span>
                </label>
              </div>
            )}
          </div>

          <Button
            data-testid="issue-card-submit-button"
            onClick={handleCreate}
            disabled={loading}
            className="w-full h-12 bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold rounded-xl"
          >
            <CreditCard className="w-4 h-4 mr-2" /> {loading ? "Minting…" : "Mint Card"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
